var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback } from 'react';
import { useDataProvider } from 'react-admin';
import { useMutation, } from 'react-query';
/**
 * Internal hook used to lazy load a node children using the dataProvider.getChildNodes() method.
 *
 * @param {string} resource The resource name, e.g. 'posts'
 * @param {Params} params The getChildNodes parameters
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 *
 * @typedef Params
 * @prop params.parentId The parent node identifier
 * @prop params.meta Optional meta parameters

 * The data structure is a flat array of TreeRecords (i.e. Records with a children field containing an array of Identifiers).
 *
 * @example // sample return value
 * [
 *   { id: 1, title: 'foo1', children: [3, 4] },
 *   { id: 2, title: 'foo2', children: [] },
 *   { id: 3, title: 'foo3', children: [5] },
 *   { id: 4, title: 'foo4', children: [] },
 *   { id: 5, title: 'foo5', children: [] },
 * ]
 */
export var useGetChildNodesCallback = function (resource, params, options) {
    var dataProvider = useDataProvider();
    var mutation = useMutation([resource, 'getChildNodes', params], function (variables) {
        return dataProvider
            .getChildNodes(resource, __assign(__assign({}, params), variables))
            .then(function (_a) {
            var data = _a.data;
            return data;
        });
    }, __assign({}, options));
    var callback = useCallback(function (parentId) {
        return mutation.mutateAsync(__assign(__assign({}, params), { parentId: parentId }));
    }, [mutation, params]);
    return callback;
};
